import { useState, useEffect } from "react";
import { doc, updateDoc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getFlagEmoji } from "../utils/flagEmoji";
import { formatDate } from "../utils/formatDate";

const FUNCTION_URL = "https://us-central1-solis-travel.cloudfunctions.net/generateItinerary";

// Days generated per request. Kept small so each call returns well inside the
// browser's fetch timeout — Safari abandons a request around 46s, and 5-day
// chunks were taking ~70s. Must stay <= MAX_CHUNK_DAYS in functions/index.js.
const ITINERARY_CHUNK_DAYS = 3;

const LOADING_MESSAGES = [
  "Mapping out your days…",
  "Finding real local spots, not tourist traps…",
  "Matching activities to everyone's interests…",
  "Pacing things so you're not rushing…",
  "Almost there — putting it all together…",
];


const normalizeDate = (input) => {
  if (!input) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
  const d = new Date(input);
  if (isNaN(d.getTime())) {
    console.warn('[normalizeDate] Could not parse:', input);
    return input;
  }
  return d.toISOString().split('T')[0];
};

export default function Itinerary({ trip, onBack, onEdit, onInvite, bookingContext, onItineraryUpdated }) {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [mcProgress, setMcProgress] = useState("");
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [itinerary, setItinerary] = useState(trip.itinerary || null);
  const [error, setError] = useState("");
  const [showBookingBanner, setShowBookingBanner] = useState(!!bookingContext);
  const [itineraryUpdatedMsg, setItineraryUpdatedMsg] = useState(false);
  const [selectedCity, setSelectedCity] = useState(0);
  const [votes, setVotes] = useState(trip.votes || {});
  const [showOptimizeConfirm, setShowOptimizeConfirm] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [voteError, setVoteError] = useState("");

  const isMultiCity = !!trip.isMultiCity;
  const uid = auth.currentUser?.uid;
  const isCreator = trip.createdBy === uid;

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "trips", trip.id), (snap) => {
      if (snap.exists()) setVotes(snap.data()?.votes || {});
    });
    return () => unsub();
  }, [trip.id]);

  useEffect(() => {
    if (!loading) { setLoadingMsgIdx(0); return; }
    const interval = setInterval(() => {
      setLoadingMsgIdx(i => (i + 1) % LOADING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [loading]);

  // ── Profile helpers ──────────────────────────────────────────────────────
  const fetchProfiles = async () => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    const memberUids = trip.memberUids || [auth.currentUser.uid];
    const profiles = [];
    for (const u of memberUids) {
      try {
        const snap = await getDoc(doc(db, "users", u));
        if (snap.exists()) {
          profiles.push({ uid: u, ...snap.data() });
        } else {
          profiles.push({ uid: u, name: "Traveler", photoURL: null });
        }
      } catch (err) {
        console.error(`fetchProfiles: failed to load profile for uid ${u}`, err);
        profiles.push({ uid: u, name: "Traveler", photoURL: null });
      }
    }
    return profiles;
  };

  const buildProfileSummary = (profiles) => {
    return profiles.length === 1
      ? `Traveler: ${profiles[0].name}, ${profiles[0].travelStyle} style, ${profiles[0].pace} pace, ${profiles[0].food} diet, ${profiles[0].budget} budget, interests: ${profiles[0].interests?.slice(0,4).join(", ")}.`
      : `Group of ${profiles.length} travelers:\n${profiles.map(p =>
          `- ${p.name || "Traveler"}: ${p.travelStyle} style, ${p.pace} pace, ${p.food} diet, ${p.budget} budget, interests: ${p.interests?.slice(0,4).join(", ")}`
        ).join("\n")}\nFind activities that match each person's interests and clearly note which traveler's interests drove each activity choice.`;
  };

  const buildBookingContextString = (b) => {
    const lines = [`Booking type: ${b.type}`];
    if (b.confirmationNumber) lines.push(`Confirmation: #${b.confirmationNumber}`);
    if (b.airline || b.name) lines.push(`Name: ${b.airline || b.name}`);
    if (b.flightNumber) lines.push(`Flight number: ${b.flightNumber}`);
    if (b.from) lines.push(`Departure airport/city: ${b.from}`);
    if (b.to) lines.push(`Arrival airport/city: ${b.to}`);
    if (b.departureDate) lines.push(`Departure: ${b.departureDate}${b.departureTime ? ` at ${b.departureTime}` : ""}`);
    if (b.arrivalDate) lines.push(`Arrival: ${b.arrivalDate}${b.arrivalTime ? ` at ${b.arrivalTime}` : ""}`);
    if (b.hotel) lines.push(`Hotel: ${b.hotel}`);
    if (b.address) lines.push(`Address: ${b.address}`);
    if (b.checkIn) lines.push(`Check-in: ${b.checkIn}`);
    if (b.checkOut) lines.push(`Check-out: ${b.checkOut}`);
    if (b.date) lines.push(`Date: ${b.date}`);
    if (b.time) lines.push(`Time: ${b.time}`);
    return lines.join("\n");
  };

  const getBannerTitle = (b) => {
    if (b.type === "flight") return "Flight booking imported";
    if (b.type === "hotel") return "Hotel booking imported";
    if (b.type === "car_rental") return "Car rental imported";
    return "Booking imported";
  };

  const getBannerSub = (b) => {
    if (b.type === "flight") {
      if (b.arrivalTime && b.arrivalDate)
        return `Your flight lands at ${b.arrivalTime} on ${formatDate(b.arrivalDate)}. Regenerate to adjust your itinerary for travel time.`;
      if (b.departureTime)
        return `Your flight departs at ${b.departureTime}. Regenerate to reflect your travel schedule.`;
      return "Regenerate your itinerary to account for flight times.";
    }
    if (b.type === "hotel") {
      const name = b.hotel || b.name;
      return `Your${name ? ` ${name}` : " hotel"} stay is confirmed${b.checkIn ? ` from ${formatDate(b.checkIn)}` : ""}. Regenerate to include nearby activities.`;
    }
    if (b.type === "activity" && b.time) {
      return `Your ${b.name || "activity"} is at ${b.time}${b.date ? ` on ${formatDate(b.date)}` : ""}. Regenerate to fit this into your schedule.`;
    }
    return "Regenerate your itinerary to reflect this booking.";
  };

  // ── Single-city generation ───────────────────────────────────────────────
  const fetchChunk = async (profiles, chunkStart, chunkSize, bookingCtxStr = null, alreadyUsed = []) => {
    const profileSummary = buildProfileSummary(profiles);
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(FUNCTION_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify({
        destination: trip.destination,
        country: trip.country,
        days: trip.days,
        startDate: trip.startDate,
        notes: trip.notes,
        profileSummary,
        chunkStart,
        chunkSize,
        bookingContext: bookingCtxStr || null,
        alreadyUsed: alreadyUsed.length > 0 ? alreadyUsed : undefined,
      }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "AI request failed");
    const text = data.content?.[0]?.text;
    if (!text) throw new Error("No response from AI");
    const clean = text.replace(/```json|```/g, "").trim();
    return JSON.parse(clean.slice(clean.indexOf("{"), clean.lastIndexOf("}") + 1));
  };

  const collectUsedNames = (chunks) => {
    const names = [];
    for (const chunk of chunks) {
      for (const day of (chunk.days || [])) {
        for (const act of (day.activities || [])) {
          if (act.name) names.push(act.name);
        }
        if (day.meals?.lunch?.name) names.push(day.meals.lunch.name);
        if (day.meals?.dinner?.name) names.push(day.meals.dinner.name);
      }
    }
    return names;
  };

  const generateAll = async (bookingCtx = null) => {
    setLoading(true);
    setError("");
    try {
      const profiles = await fetchProfiles();
      const ctxStr = bookingCtx ? buildBookingContextString(bookingCtx) : null;
      const chunkRanges = [];
      for (let i = 1; i <= trip.days; i += ITINERARY_CHUNK_DAYS) {
        chunkRanges.push({ start: i, size: Math.min(ITINERARY_CHUNK_DAYS, trip.days - i + 1) });
      }
      const chunks = [];
      for (const { start, size } of chunkRanges) {
        const alreadyUsed = collectUsedNames(chunks);
        const chunk = await fetchChunk(profiles, start, size, ctxStr, alreadyUsed);
        chunks.push(chunk);
      }
      const newItinerary = {
        summary: chunks[0].summary,
        days: chunks.flatMap(chunk => chunk.days || []),
      };
      await updateDoc(doc(db, "trips", trip.id), { itinerary: newItinerary });
      setItinerary(newItinerary);
      if (bookingCtx) {
        setShowBookingBanner(false);
        setItineraryUpdatedMsg(true);
        if (onItineraryUpdated) onItineraryUpdated();
      }
    } catch (err) {
      console.error("Itinerary generation failed:", err);
      setError(err?.message || "Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  const generateMore = async () => {
    setLoadingMore(true);
    setError("");
    try {
      const profiles = await fetchProfiles();
      const alreadyUsed = collectUsedNames([{ days: itinerary?.days || [] }]);
      const parsed = await fetchChunk(profiles, nextChunkStart, nextChunkSize, null, alreadyUsed);
      const newItinerary = {
        ...itinerary,
        days: [...(itinerary.days || []), ...(parsed.days || [])],
      };
      await updateDoc(doc(db, "trips", trip.id), { itinerary: newItinerary });
      setItinerary(newItinerary);
    } catch (err) {
      console.error("Itinerary generation failed:", err);
      setError(err?.message || "Something went wrong. Please try again.");
    }
    setLoadingMore(false);
  };

  // ── Multi-city generation ────────────────────────────────────────────────
  const fetchLegChunk = async (profiles, leg, chunkStart, chunkSize, extraCtx = null, legNotes = null, alreadyUsed = []) => {
    const profileSummary = buildProfileSummary(profiles);
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Not authenticated');
    const response = await fetch(FUNCTION_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + token },
      body: JSON.stringify({
        destination: leg.city,
        country: leg.country,
        days: leg.days,
        startDate: leg.arrivalDate,
        notes: legNotes || trip.notes,
        profileSummary,
        chunkStart,
        chunkSize,
        bookingContext: extraCtx || null,
        alreadyUsed: alreadyUsed.length > 0 ? alreadyUsed : undefined,
      }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "AI request failed");
    const text = data.content?.[0]?.text;
    if (!text) throw new Error("No response from AI");
    const clean = text.replace(/```json|```/g, "").trim();
    return JSON.parse(clean.slice(clean.indexOf("{"), clean.lastIndexOf("}") + 1));
  };

  const generateMultiCity = async (extraCtx = null) => {
    setLoading(true);
    setError("");
    try {
      const profiles = await fetchProfiles();
      const legs = trip.legs || [];
      const generatedLegs = [];
      const allUsed = [];
      for (let li = 0; li < legs.length; li++) {
        const leg = legs[li];
        const legIndex = li;
        const nextLeg = legs[li + 1] || null;
        setMcProgress(`Generating ${leg.city}... (${li + 1}/${legs.length})`);

        const isFirstLeg = legIndex === 0;
        const isLastLeg = legIndex === trip.legs.length - 1;
        const prevCity = isFirstLeg ? (trip.startingPoint || 'home') : trip.legs[legIndex - 1].city;
        const nextCity = isLastLeg ? (trip.startingPoint || 'home') : nextLeg?.city || '';

        const driveLabel = leg.driveStyle === 'morning' ? 'morning' : leg.driveStyle === 'afternoon' ? 'afternoon' : 'overnight';
        const arrivalLabel = leg.arrivalStyle === 'settle' ? 'settle in only — no activities on arrival day, full day starts next morning' : 'settle in + light activities — arrive, check in, one easy activity and dinner';

        const fullRoute = [
          trip.startingPoint || 'home',
          ...trip.legs.map(l => l.city),
          trip.startingPoint || 'home'
        ].join(' → ');

        const legNotes = [
          trip.notes || '',
          `ROAD TRIP CONTEXT: This is leg ${legIndex + 1} of ${trip.legs.length} in a road trip: ${fullRoute}`,
          `DRIVING FROM: ${prevCity} to ${leg.city}. This is a ${driveLabel} drive.`,
          `ARRIVAL DAY (${leg.arrivalDate}): ${arrivalLabel}`,
          isLastLeg ? `DEPARTURE: Last leg. Final day includes drive back to ${trip.startingPoint || 'home'} — keep last morning light.` : `DEPARTURE DAY (${nextLeg?.arrivalDate}): Morning drive to ${nextCity}. Keep this morning light — 1 activity max before departing.`,
          `Do NOT start every day from ${prevCity}. Activities should be local to ${leg.city} and surrounding area.`,
        ].filter(Boolean).join('\n');

        const chunkRanges = [];
        const legDays = leg.days || 1;
        for (let i = 1; i <= legDays; i += ITINERARY_CHUNK_DAYS) {
          chunkRanges.push({ start: i, size: Math.min(ITINERARY_CHUNK_DAYS, legDays - i + 1) });
        }
        const chunks = [];
        for (const { start, size } of chunkRanges) {
          const chunk = await fetchLegChunk(profiles, leg, start, size, extraCtx, legNotes, allUsed);
          chunks.push(chunk);
          allUsed.push(...collectUsedNames([chunk]));
        }
        generatedLegs.push({
          city: leg.city,
          country: leg.country,
          arrivalDate: leg.arrivalDate,
          departureDate: leg.departureDate,
          days: leg.days,
          transportNote: leg.transportNote,
          summary: chunks[0]?.summary || "",
          days_data: chunks.flatMap(c => c.days || []),
        });
      }
      const newItinerary = { legs: generatedLegs };
      await updateDoc(doc(db, "trips", trip.id), { itinerary: newItinerary, votes: {} });
      setItinerary(newItinerary);
      setVotes({});
      setMcProgress("");
    } catch (err) {
      console.error("Itinerary generation failed:", err);
      setError(err?.message || "Something went wrong. Please try again.");
      setMcProgress("");
    }
    setLoading(false);
  };

  // ── Voting ──────────────────────────────────────────────────────────────
  const handleVote = async (key, direction) => {
    const current = votes[key] || { thumbsUp: [], thumbsDown: [] };
    let thumbsUp = [...(current.thumbsUp || [])];
    let thumbsDown = [...(current.thumbsDown || [])];

    if (direction === "up") {
      if (thumbsUp.includes(uid)) {
        thumbsUp = thumbsUp.filter(u => u !== uid); // toggle off
      } else {
        thumbsUp = [...thumbsUp, uid];
        thumbsDown = thumbsDown.filter(u => u !== uid);
      }
    } else {
      if (thumbsDown.includes(uid)) {
        thumbsDown = thumbsDown.filter(u => u !== uid); // toggle off
      } else {
        thumbsDown = [...thumbsDown, uid];
        thumbsUp = thumbsUp.filter(u => u !== uid);
      }
    }

    const updated = { thumbsUp, thumbsDown };
    const prevVotes = votes;
    setVotes({ ...votes, [key]: updated });
    try {
      await updateDoc(doc(db, "trips", trip.id), { [`votes.${key}`]: updated });
    } catch (err) {
      console.error(err);
      setVotes(prevVotes);
      setVoteError("Couldn't save your vote. Please try again.");
      setTimeout(() => setVoteError(""), 3000);
    }
  };

  // ── Vote analytics ──────────────────────────────────────────────────────
  const computeVoteStats = () => {
    let totalVotes = 0;
    let mostLoved = null;
    let mostSkipped = null;

    const scan = (days, prefix) => {
      days.forEach((day, di) => {
        (day.activities || []).forEach((activity, ai) => {
          const v = votes[`${prefix}${di}_${ai}`] || { thumbsUp: [], thumbsDown: [] };
          totalVotes += (v.thumbsUp?.length || 0) + (v.thumbsDown?.length || 0);
          if (v.thumbsUp?.length > 0 && (!mostLoved || v.thumbsUp.length > mostLoved.count)) {
            mostLoved = { name: activity.name, count: v.thumbsUp.length };
          }
          if (v.thumbsDown?.length > 0 && (!mostSkipped || v.thumbsDown.length > mostSkipped.count)) {
            mostSkipped = { name: activity.name, count: v.thumbsDown.length };
          }
        });
      });
    };

    if (isMultiCity && itinerary?.legs) {
      itinerary.legs.forEach((leg, li) => scan(leg.days_data || [], `l${li}_`));
    } else if (itinerary?.days) {
      scan(itinerary.days, "");
    }

    return { totalVotes, mostLoved, mostSkipped };
  };

  const buildVoteContext = () => {
    const lowRated = [];
    const highRated = [];

    const scan = (days, prefix) => {
      days.forEach((day, di) => {
        (day.activities || []).forEach((activity, ai) => {
          const v = votes[`${prefix}${di}_${ai}`] || { thumbsUp: [], thumbsDown: [] };
          if ((v.thumbsDown?.length || 0) > (v.thumbsUp?.length || 0)) lowRated.push(activity.name);
          if ((v.thumbsUp?.length || 0) >= 2) highRated.push(activity.name);
        });
      });
    };

    if (isMultiCity && itinerary?.legs) {
      itinerary.legs.forEach((leg, li) => scan(leg.days_data || [], `l${li}_`));
    } else if (itinerary?.days) {
      scan(itinerary.days, "");
    }

    if (lowRated.length === 0) return null;
    let ctx = `The following activities were poorly received by the group (more thumbs down than up): ${lowRated.join(", ")}. Please replace these with alternative activities.`;
    if (highRated.length > 0) {
      ctx += ` Keep all activities that received majority thumbs up: ${highRated.join(", ")}.`;
    }
    return ctx;
  };

  // ── Optimize with votes ─────────────────────────────────────────────────
  const optimizeItinerary = async () => {
    setOptimizing(true);
    setError("");
    const voteCtx = buildVoteContext();
    try {
      if (isMultiCity) {
        await generateMultiCity(voteCtx);
      } else {
        const profiles = await fetchProfiles();
        const chunkRanges = [];
        for (let i = 1; i <= trip.days; i += ITINERARY_CHUNK_DAYS) {
          chunkRanges.push({ start: i, size: Math.min(ITINERARY_CHUNK_DAYS, trip.days - i + 1) });
        }
        const chunks = [];
        for (const { start, size } of chunkRanges) {
          const alreadyUsed = collectUsedNames(chunks);
          const chunk = await fetchChunk(profiles, start, size, voteCtx, alreadyUsed);
          chunks.push(chunk);
        }
        const newItinerary = {
          summary: chunks[0].summary,
          days: chunks.flatMap(c => c.days || []),
        };
        await updateDoc(doc(db, "trips", trip.id), { itinerary: newItinerary, votes: {} });
        setItinerary(newItinerary);
        setVotes({});
      }
      setShowOptimizeConfirm(false);
    } catch (err) {
      console.error(err);
      setError("Optimization failed. Please try again.");
    } finally {
      setOptimizing(false);
    }
  };

  // ── Derived values ──────────────────────────────────────────────────────
  const generatedDays = itinerary?.days?.length || 0;
  const hasMoreDays = !isMultiCity && generatedDays < trip.days;
  const nextChunkStart = generatedDays + 1;
  const nextChunkSize = Math.min(ITINERARY_CHUNK_DAYS, trip.days - generatedDays);
  const memberCount = trip.memberUids?.length || 1;
  const tagColors = ["#F5A623", "#1D9E75", "#C9622F", "#BA7517", "#7B3FA0"];

  const mcLegs = isMultiCity ? (itinerary?.legs || []) : [];
  const currentLegData = mcLegs[selectedCity];

  const heroDestination = isMultiCity
    ? (trip.legs || []).map(l => l.city).join(" → ")
    : trip.destination;

  const hasAnyVotes = Object.values(votes).some(
    v => (v.thumbsUp?.length || 0) + (v.thumbsDown?.length || 0) > 0
  );

  const voteStats = itinerary ? computeVoteStats() : { totalVotes: 0 };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        .it-root{min-height:100vh;background:#FAF6F0;font-family:'DM Sans',sans-serif;padding-bottom:40px;}
        .it-nav{background:white;border-bottom:0.5px solid rgba(26,23,20,0.08);padding:16px 24px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:100;}
        .it-back{font-size:14px;font-weight:500;color:rgba(26,23,20,0.55);cursor:pointer;background:none;border:none;font-family:'DM Sans',sans-serif;}
        .it-back:hover{color:#F5A623;}
        .it-nav-title{font-family:'Playfair Display',serif;font-size:16px;font-weight:600;color:#1a1714;letter-spacing:-0.02em;}
        .it-edit-btn{font-size:12px;font-weight:500;color:#1E2D4A;background:#e3eef6;border:none;border-radius:100px;padding:6px 14px;cursor:pointer;font-family:'DM Sans',sans-serif;}
        .it-hero{background:#1E2D4A;padding:28px 24px 32px;position:relative;overflow:hidden;}
        .it-hero::before{content:'';position:absolute;top:-60px;right:-60px;width:250px;height:250px;background:radial-gradient(circle,rgba(123,167,196,0.2) 0%,transparent 70%);}
        .it-hero-tag{display:inline-block;background:rgba(123,167,196,0.2);border:1px solid rgba(123,167,196,0.3);color:#7BA7C4;font-size:11px;font-weight:500;padding:4px 10px;border-radius:100px;margin-bottom:12px;text-transform:uppercase;letter-spacing:0.04em;}
        .it-hero-title{font-family:'Playfair Display',serif;font-size:26px;font-weight:600;color:white;letter-spacing:-0.02em;margin-bottom:6px;line-height:1.2;}
        .it-hero-title em{font-style:italic;color:#7BA7C4;}
        .it-hero-meta{display:flex;gap:16px;flex-wrap:wrap;}
        .it-hero-meta span{font-size:13px;color:rgba(255,255,255,0.55);font-weight:300;}
        .it-group-badge{display:inline-flex;align-items:center;gap:6px;background:rgba(255,255,255,0.1);border:1px solid rgba(255,255,255,0.15);border-radius:100px;padding:5px 12px;margin-top:10px;}
        .it-group-badge span{font-size:12px;color:rgba(255,255,255,0.7);}
        .it-progress-bar{background:rgba(255,255,255,0.1);height:4px;border-radius:2px;margin-top:16px;overflow:hidden;}
        .it-progress-fill{height:4px;background:#7BA7C4;border-radius:2px;transition:width 0.4s ease;}
        .it-progress-label{font-size:11px;color:rgba(255,255,255,0.4);margin-top:4px;}
        .it-content{padding:24px;}
        .it-generate-card{background:white;border-radius:20px;border:0.5px solid rgba(26,23,20,0.08);padding:36px 28px;text-align:center;box-shadow:0 4px 24px rgba(26,23,20,0.06);}
        .it-ai-icon{width:64px;height:64px;border-radius:20px;background:#e3eef6;display:flex;align-items:center;justify-content:center;margin:0 auto 20px;}
        .it-ai-dot-wrap{display:grid;grid-template-columns:1fr 1fr;gap:6px;}
        .it-ai-dot{width:10px;height:10px;border-radius:50%;}
        .it-generate-title{font-family:'Playfair Display',serif;font-size:22px;font-weight:600;color:#1a1714;letter-spacing:-0.02em;margin-bottom:10px;}
        .it-generate-title em{font-style:italic;color:#F5A623;}
        .it-generate-sub{font-size:14px;color:rgba(26,23,20,0.5);font-weight:300;line-height:1.6;margin-bottom:28px;max-width:340px;margin-left:auto;margin-right:auto;}
        .it-features{display:flex;flex-direction:column;gap:10px;margin-bottom:28px;text-align:left;}
        .it-feature-row{display:flex;align-items:center;gap:10px;}
        .it-feature-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0;}
        .it-feature-text{font-size:13px;color:rgba(26,23,20,0.65);}
        .it-feature-text strong{color:#1a1714;font-weight:500;}
        .it-generate-btn{width:100%;padding:15px;background:#F5A623;color:white;border:none;border-radius:14px;font-size:15px;font-weight:500;font-family:'DM Sans',sans-serif;cursor:pointer;transition:background 0.2s,transform 0.15s;display:flex;align-items:center;justify-content:center;gap:8px;}
        .it-generate-btn:hover:not(:disabled){background:#1E2D4A;transform:translateY(-1px);}
        .it-generate-btn:disabled{opacity:0.7;cursor:not-allowed;}
        .it-loading-dots{display:flex;gap:4px;align-items:center;}
        .it-loading-dot{width:6px;height:6px;border-radius:50%;background:white;animation:dotPulse 1.2s ease-in-out infinite;}
        .it-loading-dot:nth-child(2){animation-delay:0.2s;}
        .it-loading-dot:nth-child(3){animation-delay:0.4s;}
        @keyframes dotPulse{0%,80%,100%{opacity:0.3;transform:scale(0.8);}40%{opacity:1;transform:scale(1);}}
        .it-error{background:#fef0ed;border:1px solid rgba(201,98,47,0.2);border-radius:10px;padding:12px 14px;font-size:13px;color:#c9622f;margin-top:12px;text-align:left;}
        .it-summary{background:#1E2D4A;border-radius:16px;padding:18px 20px;margin-bottom:20px;}
        .it-summary-label{font-size:11px;color:rgba(255,255,255,0.5);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:8px;}
        .it-summary-text{font-size:14px;color:rgba(255,255,255,0.85);line-height:1.6;font-weight:300;font-style:italic;font-family:'Playfair Display',serif;}
        .it-day-card{background:white;border-radius:18px;border:0.5px solid rgba(26,23,20,0.08);overflow:hidden;margin-bottom:16px;box-shadow:0 2px 16px rgba(26,23,20,0.04);}
        .it-day-header{padding:16px 20px;border-bottom:0.5px solid rgba(26,23,20,0.06);display:flex;justify-content:space-between;align-items:center;}
        .it-day-num{font-size:11px;font-weight:500;color:#F5A623;text-transform:uppercase;letter-spacing:0.06em;margin-bottom:2px;}
        .it-day-title{font-family:'Playfair Display',serif;font-size:17px;font-weight:600;color:#1a1714;}
        .it-day-date{font-size:12px;color:rgba(26,23,20,0.4);}
        .it-activity{display:flex;gap:14px;padding:14px 20px;border-bottom:0.5px solid rgba(26,23,20,0.05);}
        .it-activity:last-child{border-bottom:none;}
        .it-activity-time-col{display:flex;flex-direction:column;align-items:center;width:48px;flex-shrink:0;}
        .it-activity-time{font-size:10px;color:rgba(26,23,20,0.4);white-space:nowrap;margin-bottom:4px;}
        .it-activity-line{width:1px;flex:1;background:rgba(26,23,20,0.08);min-height:20px;}
        .it-activity-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;margin-top:2px;}
        .it-activity-body{flex:1;}
        .it-activity-name{font-size:14px;font-weight:500;color:#1a1714;margin-bottom:3px;}
        .it-activity-desc{font-size:12px;color:rgba(26,23,20,0.55);line-height:1.5;margin-bottom:6px;font-weight:300;}
        .it-why-chosen{display:flex;flex-wrap:wrap;gap:5px;margin-top:6px;}
        .it-why-tag{font-size:10px;font-weight:500;padding:2px 8px;border-radius:100px;color:white;}
        .it-local-tip{display:flex;align-items:flex-start;gap:6px;margin-top:6px;}
        .it-tip-badge{font-size:10px;font-weight:500;padding:2px 7px;border-radius:100px;background:#FAEEDA;color:#633806;white-space:nowrap;flex-shrink:0;}
        .it-tip-text{font-size:11px;color:rgba(26,23,20,0.55);font-style:italic;line-height:1.4;}
        .it-meals{background:#FAF6F0;padding:14px 20px;display:grid;grid-template-columns:1fr 1fr;gap:10px;}
        .it-meal{background:white;border-radius:12px;padding:12px;border:0.5px solid rgba(26,23,20,0.06);}
        .it-meal-type{font-size:10px;font-weight:500;color:rgba(26,23,20,0.4);text-transform:uppercase;letter-spacing:0.06em;margin-bottom:4px;}
        .it-meal-name{font-size:13px;font-weight:500;color:#1a1714;margin-bottom:2px;}
        .it-meal-desc{font-size:11px;color:rgba(26,23,20,0.5);line-height:1.4;font-weight:300;}
        .it-more-btn{width:100%;padding:14px;background:#e3eef6;color:#F5A623;border:none;border-radius:14px;font-size:14px;font-weight:500;font-family:'DM Sans',sans-serif;cursor:pointer;transition:background 0.2s;margin-top:8px;display:flex;align-items:center;justify-content:center;gap:8px;}
        .it-more-btn:hover:not(:disabled){background:#F5A623;color:white;}
        .it-more-btn:disabled{opacity:0.6;cursor:not-allowed;}
        .it-regen-btn{width:100%;padding:13px;border:1.5px solid rgba(26,23,20,0.12);border-radius:12px;background:white;font-size:14px;font-weight:500;color:rgba(26,23,20,0.6);cursor:pointer;font-family:'DM Sans',sans-serif;transition:all 0.2s;margin-top:8px;}
        .it-regen-btn:hover{border-color:#F5A623;color:#F5A623;}
        .it-loading-info{font-size:12px;color:rgba(26,23,20,0.4);text-align:center;margin-top:12px;font-weight:300;}
        .it-booking-banner{background:#FEF0DC;border:1px solid rgba(245,166,35,0.35);border-radius:16px;padding:16px 18px;margin-bottom:16px;animation:fadeUp 0.35s ease forwards;}
        .it-booking-banner-title{font-size:14px;font-weight:500;color:#1E2D4A;margin-bottom:4px;}
        .it-booking-banner-sub{font-size:13px;color:rgba(30,45,74,0.7);line-height:1.55;margin-bottom:12px;font-weight:300;}
        .it-booking-banner-actions{display:flex;gap:8px;}
        .it-booking-banner-btn{padding:10px 18px;background:#F5A623;color:#1E2D4A;border:none;border-radius:10px;font-size:13px;font-weight:500;font-family:'DM Sans',sans-serif;cursor:pointer;transition:background 0.2s;}
        .it-booking-banner-btn:hover:not(:disabled){background:#C47A0A;color:white;}
        .it-booking-banner-btn:disabled{opacity:0.6;cursor:not-allowed;}
        .it-booking-banner-dismiss{padding:10px 14px;background:white;color:rgba(26,23,20,0.55);border:1px solid rgba(26,23,20,0.12);border-radius:10px;font-size:13px;font-weight:500;font-family:'DM Sans',sans-serif;cursor:pointer;}
        .it-success-msg{background:#E1F5EE;border:1px solid rgba(29,158,117,0.2);border-radius:12px;padding:14px 16px;margin-bottom:16px;display:flex;align-items:center;gap:10px;font-size:13px;color:#085041;font-weight:500;animation:fadeUp 0.35s ease forwards;}

        /* Multi-city city tabs */
        .it-city-tabs{background:white;border-bottom:0.5px solid rgba(26,23,20,0.08);padding:0 20px;display:flex;gap:0;overflow-x:auto;-webkit-overflow-scrolling:touch;}
        .it-city-tab{padding:14px 16px;font-size:13px;font-weight:500;color:rgba(26,23,20,0.45);border:none;background:none;cursor:pointer;font-family:'DM Sans',sans-serif;white-space:nowrap;border-bottom:3px solid transparent;transition:all 0.2s;}
        .it-city-tab.active{color:#1E2D4A;border-bottom-color:#F5A623;}
        .it-city-tab:hover{color:#1E2D4A;}

        /* Travel day card */
        .it-travel-card{background:#F5A623;border-radius:14px;padding:14px 18px;margin-bottom:16px;display:flex;align-items:center;gap:12px;}
        .it-travel-icon{font-size:22px;flex-shrink:0;}
        .it-travel-text{font-size:14px;font-weight:500;color:#1E2D4A;}
        .it-travel-note{font-size:12px;color:rgba(30,45,74,0.65);margin-top:2px;font-weight:300;}

        /* Voting buttons */
        .it-vote-row{display:flex;align-items:center;gap:6px;margin-top:9px;flex-wrap:wrap;}
        .it-vote-btn{display:inline-flex;align-items:center;gap:4px;padding:5px 11px;border-radius:100px;border:1.5px solid rgba(26,23,20,0.1);background:white;font-size:12px;font-family:'DM Sans',sans-serif;cursor:pointer;transition:all 0.15s;color:rgba(26,23,20,0.45);line-height:1;}
        .it-vote-btn:hover{border-color:#F5A623;color:#C47A0A;}
        .it-vote-btn.voted-up{background:#FEF0DC;border-color:#F5A623;color:#C47A0A;font-weight:600;}
        .it-vote-btn.voted-down{background:#FEF0DC;border-color:#F5A623;color:#C47A0A;font-weight:600;}
        .it-vote-count{font-size:11px;min-width:8px;text-align:center;}
        .it-vote-fav-badge{display:inline-flex;align-items:center;gap:3px;font-size:10px;font-weight:600;background:#1E2D4A;color:white;padding:2px 8px;border-radius:100px;white-space:nowrap;}
        .it-vote-low-badge{display:inline-flex;align-items:center;gap:3px;font-size:10px;font-weight:600;background:#FEF0DC;color:#C47A0A;border:1px solid rgba(245,166,35,0.4);padding:2px 8px;border-radius:100px;white-space:nowrap;}

        /* Vote summary card */
        .it-vote-summary{background:white;border-radius:16px;border:0.5px solid rgba(26,23,20,0.08);padding:18px 20px;margin-top:16px;box-shadow:0 2px 12px rgba(26,23,20,0.04);}
        .it-vote-summary-title{font-size:11px;font-weight:600;color:rgba(26,23,20,0.4);text-transform:uppercase;letter-spacing:0.07em;margin-bottom:14px;}
        .it-vote-stat-grid{display:flex;gap:10px;flex-wrap:wrap;}
        .it-vote-stat{flex:1;min-width:90px;background:#FAF6F0;border-radius:10px;padding:10px 12px;}
        .it-vote-stat-label{font-size:10px;color:rgba(26,23,20,0.4);margin-bottom:4px;font-weight:500;}
        .it-vote-stat-val{font-size:13px;font-weight:500;color:#1a1714;line-height:1.3;}

        /* Optimize button */
        .it-optimize-btn{width:100%;padding:14px;background:#F5A623;color:#1E2D4A;border:none;border-radius:14px;font-size:14px;font-weight:500;font-family:'DM Sans',sans-serif;cursor:pointer;transition:all 0.2s;margin-top:12px;display:flex;align-items:center;justify-content:center;gap:6px;}
        .it-optimize-btn:hover:not(:disabled){background:#C47A0A;color:white;transform:translateY(-1px);}
        .it-optimize-btn:disabled{opacity:0.6;cursor:not-allowed;transform:none;}

        /* Optimize confirm */
        .it-optimize-confirm{background:white;border:1.5px solid rgba(245,166,35,0.45);border-radius:16px;padding:20px;margin-top:12px;animation:fadeUp 0.25s ease forwards;}
        .it-optimize-confirm-title{font-size:14px;font-weight:500;color:#1a1714;margin-bottom:6px;}
        .it-optimize-confirm-sub{font-size:13px;color:rgba(26,23,20,0.55);line-height:1.55;margin-bottom:16px;font-weight:300;}
        .it-optimize-confirm-actions{display:flex;gap:8px;}
        .it-optimize-go{flex:1;padding:12px;background:#F5A623;color:#1E2D4A;border:none;border-radius:10px;font-size:13px;font-weight:500;font-family:'DM Sans',sans-serif;cursor:pointer;transition:all 0.15s;}
        .it-optimize-go:hover:not(:disabled){background:#C47A0A;color:white;}
        .it-optimize-go:disabled{opacity:0.6;cursor:not-allowed;}
        .it-optimize-cancel{padding:12px 16px;background:white;color:rgba(26,23,20,0.5);border:1.5px solid rgba(26,23,20,0.12);border-radius:10px;font-size:13px;font-weight:500;font-family:'DM Sans',sans-serif;cursor:pointer;}

        @keyframes fadeUp{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
      `}</style>

      <div className="it-root">
        <div className="it-nav">
          <button className="it-back" onClick={onBack}>← Back</button>
          <span className="it-nav-title">{trip.name}</span>
          <button className="it-edit-btn" onClick={onEdit}>Edit ✏️</button>
        </div>

        <div className="it-hero">
          <div className="it-hero-tag">{isMultiCity ? "Multi-City Itinerary" : "Your Itinerary"}</div>
          <h1 className="it-hero-title">
            {heroDestination}<br /><em>{trip.days} days of adventure</em>
          </h1>
          <div className="it-hero-meta">
            <span>📅 {formatDate(trip.startDate)} → {formatDate(trip.endDate)}</span>
            {!isMultiCity && <span>{getFlagEmoji(trip.countryCode) || '📍'} {trip.country}</span>}
            {isMultiCity && <span>🏙 {(trip.legs || []).length} cities</span>}
          </div>
          {memberCount > 1 && (
            <div className="it-group-badge">
              <span>👥 {memberCount} travelers · built for your group</span>
            </div>
          )}
          {itinerary && !isMultiCity && (
            <>
              <div className="it-progress-bar">
                <div className="it-progress-fill" style={{width:`${(generatedDays/trip.days)*100}%`}}></div>
              </div>
              <div className="it-progress-label">{generatedDays} of {trip.days} days planned</div>
            </>
          )}
        </div>

        {/* City tabs (multi-city with itinerary) */}
        {isMultiCity && itinerary && mcLegs.length > 0 && (
          <div className="it-city-tabs">
            {mcLegs.map((leg, idx) => (
              <button
                key={idx}
                className={`it-city-tab ${selectedCity === idx ? "active" : ""}`}
                onClick={() => setSelectedCity(idx)}
              >
                {leg.city} ({leg.days}d)
              </button>
            ))}
          </div>
        )}

        <div className="it-content">
          {itineraryUpdatedMsg && (
            <div className="it-success-msg">
              <span>✨</span>
              <span>Itinerary updated to reflect your booking! ✨</span>
              <button onClick={() => setItineraryUpdatedMsg(false)} style={{marginLeft:'auto',background:'none',border:'none',fontSize:18,cursor:'pointer',color:'#085041',lineHeight:1}}>×</button>
            </div>
          )}

          {showBookingBanner && bookingContext && (
            <div className="it-booking-banner">
              <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:8}}>
                <span style={{fontSize:22,flexShrink:0}}>
                  {bookingContext.type === 'flight' ? '✈️' : bookingContext.type === 'hotel' ? '🏨' : bookingContext.type === 'car_rental' ? '🚗' : '🎭'}
                </span>
                <div>
                  <div className="it-booking-banner-title">{getBannerTitle(bookingContext)}</div>
                  <div className="it-booking-banner-sub">{getBannerSub(bookingContext)}</div>
                </div>
              </div>
              <div className="it-booking-banner-actions">
                <button
                  className="it-booking-banner-btn"
                  onClick={() => generateAll(bookingContext)}
                  disabled={loading}
                >
                  {loading ? "Updating itinerary…" : "Update Itinerary ✨"}
                </button>
                <button
                  className="it-booking-banner-dismiss"
                  onClick={() => { setShowBookingBanner(false); if (onItineraryUpdated) onItineraryUpdated(); }}
                >
                  Keep as is
                </button>
              </div>
              {error && <div className="it-error" style={{marginTop:10}}>{error}</div>}
            </div>
          )}

          {/* ── Generate prompt ── */}
          {!itinerary ? (
            <div className="it-generate-card">
              <div className="it-ai-icon">
                <div className="it-ai-dot-wrap">
                  <div className="it-ai-dot" style={{background:'#F5A623'}}></div>
                  <div className="it-ai-dot" style={{background:'#7BA7C4'}}></div>
                  <div className="it-ai-dot" style={{background:'#1D9E75'}}></div>
                  <div className="it-ai-dot" style={{background:'#C9622F'}}></div>
                </div>
              </div>

              <h2 className="it-generate-title">
                {isMultiCity
                  ? <>Plan your <em>multi-city trip</em></>
                  : memberCount > 1 ? <>Plan your <em>group trip</em></> : <>Let us plan your <em>perfect trip</em></>
                }
              </h2>
              <p className="it-generate-sub">
                {isMultiCity
                  ? `Solis generates a day-by-day itinerary for each city — ${(trip.legs || []).map(l => l.city).join(", ")} — sequentially.`
                  : memberCount > 1
                    ? `Solis reads all ${memberCount} traveler profiles and builds an itinerary that works for everyone — showing exactly why each activity was chosen.`
                    : "Solis reads your traveler profile and builds a personalised day-by-day itinerary — showing exactly why each activity was chosen for you."}
              </p>

              <div className="it-features">
                <div className="it-feature-row">
                  <div className="it-feature-dot" style={{background:'#F5A623'}}></div>
                  <span className="it-feature-text"><strong>Personalised picks</strong> — each activity tagged with whose interests it matches</span>
                </div>
                <div className="it-feature-row">
                  <div className="it-feature-dot" style={{background:'#1D9E75'}}></div>
                  <span className="it-feature-text"><strong>Local tips</strong> woven into every day of the plan</span>
                </div>
                <div className="it-feature-row">
                  <div className="it-feature-dot" style={{background:'#C9622F'}}></div>
                  <span className="it-feature-text"><strong>Restaurant picks</strong> matched to dietary preferences</span>
                </div>
                {isMultiCity && (
                  <div className="it-feature-row">
                    <div className="it-feature-dot" style={{background:'#7B3FA0'}}></div>
                    <span className="it-feature-text"><strong>Travel days</strong> shown between each city</span>
                  </div>
                )}
                <div className="it-feature-row">
                  <div className="it-feature-dot" style={{background:'#BA7517'}}></div>
                  <span className="it-feature-text"><strong>Group voting</strong> — 👍 👎 every activity, then optimize with one tap</span>
                </div>
              </div>

              {onInvite && memberCount === 1 && (
                <p style={{fontSize:13,color:'rgba(26,23,20,0.5)',textAlign:'center',margin:'0 0 16px',lineHeight:1.5}}>
                  Traveling with others?{" "}
                  <button
                    onClick={async () => { await onInvite(); setInviteCopied(true); setTimeout(() => setInviteCopied(false), 2500); }}
                    style={{background:'none',border:'none',padding:0,color:'#C9622F',fontWeight:600,fontSize:13,cursor:'pointer',textDecoration:'underline'}}
                  >
                    {inviteCopied ? "✓ Link copied!" : "Invite friends"}
                  </button>
                  {" "}first so your itinerary fits everyone's tastes.
                </p>
              )}

              <button className="it-generate-btn" onClick={isMultiCity ? () => generateMultiCity() : generateAll} disabled={loading}>
                {loading ? (
                  <>
                    <div className="it-loading-dots">
                      <div className="it-loading-dot"></div>
                      <div className="it-loading-dot"></div>
                      <div className="it-loading-dot"></div>
                    </div>
                    {mcProgress || LOADING_MESSAGES[loadingMsgIdx]}
                  </>
                ) : isMultiCity
                    ? `✨ Generate itinerary for ${(trip.legs||[]).length} cities`
                    : memberCount > 1 ? "✨ Generate group itinerary" : "✨ Generate my itinerary"}
              </button>

              {error && <div className="it-error">{error}</div>}
              {loading && (
                <p className="it-loading-info">
                  {isMultiCity
                    ? mcProgress || `Generating city by city — this takes about 2–4 minutes ✨`
                    : trip.days > 5
                      ? `Generating day by day — this takes about 1–2 minutes ✨`
                      : memberCount > 1 ? `Reading ${memberCount} profiles and planning...` : "This takes about a minute ✨"}
                </p>
              )}
            </div>

          ) : isMultiCity ? (
            /* ── Multi-city itinerary view ── */
            <>
              {currentLegData && (
                <>
                  {selectedCity > 0 && (
                    <div className="it-travel-card">
                      <span className="it-travel-icon">✈️</span>
                      <div>
                        <div className="it-travel-text">
                          Travel Day — {mcLegs[selectedCity - 1].city} → {currentLegData.city}
                        </div>
                        {currentLegData.transportNote && (
                          <div className="it-travel-note">{currentLegData.transportNote}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {currentLegData.summary && (
                    <div className="it-summary">
                      <div className="it-summary-label">{currentLegData.city} overview</div>
                      <div className="it-summary-text">{currentLegData.summary}</div>
                    </div>
                  )}

                  {(currentLegData.days_data || []).map((day, index) => (
                    <DayCard
                      key={index}
                      day={day}
                      dayIdx={index}
                      tagColors={tagColors}
                      votes={votes}
                      onVote={handleVote}
                      uid={uid}
                      keyPrefix={`l${selectedCity}_`}
                      bookings={trip.bookings || []}
                      legs={trip.legs || []}
                    />
                  ))}
                </>
              )}

              <VoteSummaryCard stats={voteStats} />

              {isCreator && hasAnyVotes && !showOptimizeConfirm && (
                <button className="it-optimize-btn" onClick={() => setShowOptimizeConfirm(true)} disabled={optimizing}>
                  ✨ Optimize with votes
                </button>
              )}
              {isCreator && showOptimizeConfirm && (
                <OptimizeConfirm
                  optimizing={optimizing}
                  onConfirm={optimizeItinerary}
                  onCancel={() => setShowOptimizeConfirm(false)}
                />
              )}
              {voteError && <div className="it-error">{voteError}</div>}
              {error && <div className="it-error">{error}</div>}

              <button className="it-regen-btn" onClick={() => setItinerary(null)}>↺ Regenerate all cities</button>
            </>

          ) : (
            /* ── Single-city itinerary view ── */
            <>
              {itinerary.summary && (
                <div className="it-summary">
                  <div className="it-summary-label">Trip overview</div>
                  <div className="it-summary-text">{itinerary.summary}</div>
                </div>
              )}

              {itinerary.days?.map((day, index) => (
                <DayCard
                  key={index}
                  day={day}
                  dayIdx={index}
                  tagColors={tagColors}
                  votes={votes}
                  onVote={handleVote}
                  uid={uid}
                  keyPrefix=""
                  bookings={trip.bookings || []}
                  legs={trip.legs || []}
                />
              ))}

              {hasMoreDays && (
                <button className="it-more-btn" onClick={generateMore} disabled={loadingMore}>
                  {loadingMore ? (
                    <>
                      <div className="it-loading-dots">
                        <div className="it-loading-dot" style={{background:'#F5A623'}}></div>
                        <div className="it-loading-dot" style={{background:'#F5A623'}}></div>
                        <div className="it-loading-dot" style={{background:'#F5A623'}}></div>
                      </div>
                      Planning days {nextChunkStart}–{nextChunkStart+nextChunkSize-1}...
                    </>
                  ) : `✨ Generate days ${nextChunkStart}–${nextChunkStart+nextChunkSize-1}`}
                </button>
              )}

              <VoteSummaryCard stats={voteStats} />

              {isCreator && hasAnyVotes && !showOptimizeConfirm && (
                <button className="it-optimize-btn" onClick={() => setShowOptimizeConfirm(true)} disabled={optimizing}>
                  ✨ Optimize with votes
                </button>
              )}
              {isCreator && showOptimizeConfirm && (
                <OptimizeConfirm
                  optimizing={optimizing}
                  onConfirm={optimizeItinerary}
                  onCancel={() => setShowOptimizeConfirm(false)}
                />
              )}
              {voteError && <div className="it-error">{voteError}</div>}
              {error && <div className="it-error">{error}</div>}

              <button className="it-regen-btn" onClick={() => setItinerary(null)}>↺ Start over</button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function getBookingsForDay(bookings, dayDate) {
  if (!bookings || !dayDate) return [];
  const normalizedDay = normalizeDate(dayDate);
  return bookings.filter(b => {
    if (b.type === 'flight')
      return normalizeDate(b.departureDate) === normalizedDay || normalizeDate(b.arrivalDate) === normalizedDay;
    if (b.type === 'hotel')
      return normalizeDate(b.checkIn) === normalizedDay || normalizeDate(b.checkOut) === normalizedDay;
    return normalizeDate(b.date) === normalizedDay || normalizeDate(b.activityDate) === normalizedDay;
  });
}

function BookingInlineCard({ booking: b, dayDate }) {
  const emoji = b.type === 'flight' ? '✈️' : b.type === 'hotel' ? '🏨' : b.type === 'car_rental' ? '🚗' : '🎭';
  const title = b.type === 'flight' ? (b.airline || b.name || 'Flight') :
                b.type === 'hotel' ? (b.hotel || b.name || 'Hotel') : (b.name || 'Booking');

  let detail = '';
  if (b.type === 'flight') {
    const route = [b.from, b.to].filter(Boolean).join(' → ');
    if (b.departureDate === dayDate && b.departureTime) detail = `Departs ${b.departureTime}${route ? ' · ' + route : ''}`;
    else if (b.arrivalDate === dayDate && b.arrivalTime) detail = `Arrives ${b.arrivalTime}${route ? ' · ' + route : ''}`;
    else if (route) detail = route;
  } else if (b.type === 'hotel') {
    detail = b.checkIn === dayDate ? 'Check-in' : 'Check-out';
  } else if (b.time) {
    detail = b.time;
  }

  return (
    <div style={{margin:'10px 16px 0',background:'#FAF6F0',borderLeft:'3px solid #F5A623',borderRadius:'0 10px 10px 0',padding:'10px 14px'}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:5}}>
        <span style={{fontSize:11,fontWeight:600,background:'#FEF0DC',color:'#C47A0A',padding:'2px 8px',borderRadius:100}}>
          {emoji} Confirmed
        </span>
      </div>
      <div style={{fontSize:13,fontWeight:500,color:'#1a1714',marginBottom:2}}>{title}</div>
      {detail && <div style={{fontSize:12,color:'rgba(26,23,20,0.55)'}}>{detail}</div>}
      {b.confirmationNumber && (
        <div style={{fontSize:11,color:'rgba(26,23,20,0.4)',marginTop:3}}>Conf: {b.confirmationNumber}</div>
      )}
    </div>
  );
}

function DayCard({ day, dayIdx, tagColors, votes, onVote, uid, keyPrefix, bookings = [], legs = [] }) {
  const isArrivalDay   = legs.some(l => l.arrivalDate   === day.date);
  const isDepartureDay = legs.some(l => l.departureDate === day.date);

  return (
    <div className="it-day-card">
      <div className="it-day-header">
        <div>
          <div className="it-day-num">Day {day.day}</div>
          <div className="it-day-title">{day.title}</div>
        </div>
        <div className="it-day-date">{formatDate(day.date)}</div>
      </div>

      {(isArrivalDay || isDepartureDay) && (
        <div style={{background:'#FFF3E0',color:'#F5A623',fontSize:13,padding:8,borderRadius:8,marginBottom:12,textAlign:'center',margin:'8px 16px 0'}}>
          🚗 {isArrivalDay && isDepartureDay ? 'Driving Day — Arrival & Departure' : isArrivalDay ? 'Driving Day — Arriving today' : 'Driving Day — Departing today'}
        </div>
      )}

      {getBookingsForDay(bookings, day.date).map((b, bi) => (
        <BookingInlineCard key={bi} booking={b} dayDate={day.date} />
      ))}

      {day.activities?.map((activity, aIdx) => {
        const voteKey = `${keyPrefix}${dayIdx}_${aIdx}`;
        const v = votes[voteKey] || { thumbsUp: [], thumbsDown: [] };
        const upCount = v.thumbsUp?.length || 0;
        const downCount = v.thumbsDown?.length || 0;
        const userVotedUp = uid && v.thumbsUp?.includes(uid);
        const userVotedDown = uid && v.thumbsDown?.includes(uid);
        const isGroupFav = upCount >= 3;
        const isLowInterest = downCount > upCount && (upCount + downCount) > 0;

        return (
          <div key={aIdx} className="it-activity">
            <div className="it-activity-time-col">
              <span className="it-activity-time">{activity.time}</span>
              <div className="it-activity-line"></div>
            </div>
            <div style={{display:'flex',gap:10,flex:1,alignItems:'flex-start'}}>
              <div className="it-activity-dot" style={{
                background: tagColors[aIdx % tagColors.length],
                marginTop:4, flexShrink:0
              }}></div>
              <div className="it-activity-body">
                {/* Activity flag badges */}
                {(isGroupFav || isLowInterest) && (
                  <div style={{display:'flex',gap:5,marginBottom:5,flexWrap:'wrap'}}>
                    {isGroupFav && (
                      <span className="it-vote-fav-badge">⭐ Group favourite</span>
                    )}
                    {isLowInterest && (
                      <span className="it-vote-low-badge">⚠️ Low interest</span>
                    )}
                  </div>
                )}

                <div className="it-activity-name">{activity.name}</div>
                <div className="it-activity-desc">{activity.description}</div>

                {activity.whyChosen?.length > 0 && (
                  <div className="it-why-chosen">
                    {activity.whyChosen.map((w, wi) => (
                      <span key={wi} className="it-why-tag" style={{background: tagColors[wi % tagColors.length]}}>
                        ✓ {w.name}: {w.interest}
                      </span>
                    ))}
                  </div>
                )}

                {activity.localTip && (
                  <div className="it-local-tip">
                    <span className="it-tip-badge">Local tip</span>
                    <span className="it-tip-text">{activity.localTip}</span>
                  </div>
                )}

                {/* Voting row */}
                <div className="it-vote-row">
                  <button
                    className={`it-vote-btn${userVotedUp ? " voted-up" : ""}`}
                    onClick={() => onVote(voteKey, "up")}
                    title="Thumbs up"
                  >
                    👍 <span className="it-vote-count">{upCount > 0 ? upCount : ""}</span>
                  </button>
                  <button
                    className={`it-vote-btn${userVotedDown ? " voted-down" : ""}`}
                    onClick={() => onVote(voteKey, "down")}
                    title="Thumbs down"
                  >
                    👎 <span className="it-vote-count">{downCount > 0 ? downCount : ""}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {day.meals && (
        <div className="it-meals">
          {day.meals.lunch && (
            <div className="it-meal">
              <div className="it-meal-type">Lunch</div>
              <div className="it-meal-name">{day.meals.lunch.name}</div>
              <div className="it-meal-desc">{day.meals.lunch.description}</div>
            </div>
          )}
          {day.meals.dinner && (
            <div className="it-meal">
              <div className="it-meal-type">Dinner</div>
              <div className="it-meal-name">{day.meals.dinner.name}</div>
              <div className="it-meal-desc">{day.meals.dinner.description}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VoteSummaryCard({ stats }) {
  const { totalVotes, mostLoved, mostSkipped } = stats;
  if (totalVotes < 3) return null;

  return (
    <div className="it-vote-summary">
      <div className="it-vote-summary-title">Group Votes</div>
      <div className="it-vote-stat-grid">
        <div className="it-vote-stat">
          <div className="it-vote-stat-label">Total votes</div>
          <div className="it-vote-stat-val">{totalVotes}</div>
        </div>
        {mostLoved && mostLoved.count > 0 && (
          <div className="it-vote-stat">
            <div className="it-vote-stat-label">⭐ Most loved</div>
            <div className="it-vote-stat-val">{mostLoved.name}</div>
          </div>
        )}
        {mostSkipped && mostSkipped.count > 0 && (
          <div className="it-vote-stat">
            <div className="it-vote-stat-label">⚠️ Most skipped</div>
            <div className="it-vote-stat-val">{mostSkipped.name}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function OptimizeConfirm({ optimizing, onConfirm, onCancel }) {
  return (
    <div className="it-optimize-confirm">
      <div className="it-optimize-confirm-title">Regenerate with vote feedback?</div>
      <div className="it-optimize-confirm-sub">
        This will replace low-rated activities with alternatives while keeping the ones your group loved. All votes will be cleared after regeneration.
      </div>
      <div className="it-optimize-confirm-actions">
        <button className="it-optimize-go" onClick={onConfirm} disabled={optimizing}>
          {optimizing ? (
            <span style={{display:'flex',alignItems:'center',gap:6,justifyContent:'center'}}>
              <span style={{display:'flex',gap:3}}>
                {[0,1,2].map(i => (
                  <span key={i} style={{
                    width:5,height:5,borderRadius:'50%',background:'#1E2D4A',
                    display:'inline-block',
                    animation:`dotPulse 1.2s ease-in-out ${i*0.2}s infinite`
                  }}/>
                ))}
              </span>
              Optimizing...
            </span>
          ) : "✨ Yes, optimize it"}
        </button>
        <button className="it-optimize-cancel" onClick={onCancel} disabled={optimizing}>Cancel</button>
      </div>
    </div>
  );
}
